/* @jsxImportSource @emotion/react */
import React from 'react'
import classNames from 'classnames'

import type { Icon } from '@slite/icons'
import { ChevronRightIcon } from '@slite/icons'

import type { ButtonSize, ButtonVariant } from './style'
import { buttonCss } from './style'

export type { ButtonSize, ButtonVariant }

export type ButtonProps = {
  variant?: ButtonVariant
  size?: ButtonSize
  withChevron?: boolean
  isActive?: boolean
  icon?: Icon
  href?: string
  target?: string
  rel?: string
  /** Filename to save `href` as, for buttons that link to a file. */
  download?: string
  type?: 'button' | 'submit'
  disabled?: boolean
  onClick?: (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void
  className?: string
  style?: React.CSSProperties
  'aria-label'?: string
  id?: string
  children: React.ReactNode
}

function iconSizeFor(size: ButtonSize) {
  if (size === 'small') return 14
  if (size === 'huge') return 18
  return 16
}

export function Button({
  variant = 'primary',
  size = 'default',
  withChevron = false,
  isActive = false,
  icon: IconComp,
  href,
  target,
  rel,
  download,
  type = 'button',
  disabled,
  onClick,
  className,
  style,
  'aria-label': ariaLabel,
  id,
  children,
}: ButtonProps) {
  const css = buttonCss(variant, size)
  const mergedClassName = classNames(className, { active: isActive })
  const content = (
    <>
      {!!IconComp && (
        <IconComp size={iconSizeFor(size)} className="button-icon" />
      )}
      <span>{children}</span>
      {withChevron && <ChevronRightIcon size={14} className="button-chevron" />}
    </>
  )
  if (href) {
    return (
      <a
        href={href}
        target={target}
        rel={rel}
        download={download}
        onClick={onClick}
        className={mergedClassName}
        style={style}
        aria-label={ariaLabel}
        id={id}
        css={css}
      >
        {content}
      </a>
    )
  }
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      className={mergedClassName}
      style={style}
      aria-label={ariaLabel}
      id={id}
      css={css}
    >
      {content}
    </button>
  )
}
